/**
 * The instruments of the music, as voice recipes (see synth.ts): a note of the lead, the
 * accompaniment or the bass, and the drums of the three kits. Kicks and toms carry a short
 * click on top of the low thump: phone speakers cannot play 50 Hz, but they play the click.
 */
import {
  held,
  midiToHz,
  type NoiseVoice,
  pluck,
  type ToneVoice,
  type VoiceSpec,
  type Wave,
} from '../synth';
import type { DrumKind } from './composer';
import type { ArpInstrument, BassInstrument, DrumKit, LeadInstrument } from './styles';

/** Peak gains of the parts before the music bus (balanced by ear and by measurement). */
const LEVEL = {
  lead: 0.15,
  arp: 0.06,
  bass: 0.2,
} as const;

export function leadVoices(
  instrument: LeadInstrument,
  midi: number,
  seconds: number,
  velocity: number,
): readonly VoiceSpec[] {
  const freq = midiToHz(midi);
  const gain = LEVEL.lead * velocity;
  switch (instrument) {
    case 'bell':
      return [
        tone('triangle', freq, gain * 1.3, pluck(Math.max(0.35, seconds * 1.5))),
        tone('sine', freq * 2, gain * 0.45, pluck(0.25)),
      ];
    case 'triangle':
      return [tone('triangle', freq, gain * 1.5, held(seconds, 0.75, 0.08))];
    case 'organ':
      // Swells in a little and holds, like a church organ.
      return [tone('organ', freq, gain * 0.85, held(seconds, 0.85, 0.12, 0.025))];
    case 'celesta':
      // A music-box ping: a pure tone with a glassy partial two octaves up.
      return [
        tone('sine', freq, gain * 1.7, pluck(Math.max(0.45, seconds * 1.8))),
        tone('sine', freq * 4, gain * 0.22, pluck(0.12)),
      ];
    default:
      return [tone(instrument, freq, gain, held(seconds, 0.6, 0.06))];
  }
}

export function arpVoices(
  instrument: ArpInstrument,
  midi: number,
  seconds: number,
  velocity: number,
): readonly VoiceSpec[] {
  const freq = midiToHz(midi);
  const gain = LEVEL.arp * velocity;
  const decay = Math.min(0.25, seconds + 0.06);
  if (instrument === 'bell') {
    return [tone('triangle', freq, gain * 1.4, pluck(Math.max(0.2, decay * 1.5)))];
  }
  if (instrument === 'celesta') {
    // An octave up, ringing a little longer than the step: a music box.
    return [tone('sine', freq * 2, gain * 1.6, pluck(Math.max(0.22, decay * 1.6)))];
  }
  return [tone(instrument, freq, gain * (instrument === 'triangle' ? 1.5 : 1), pluck(decay))];
}

export function bassVoices(
  instrument: BassInstrument,
  midi: number,
  seconds: number,
  velocity: number,
): readonly VoiceSpec[] {
  // A triangle bass is almost pure fundamental: an octave up it stays audible on phones.
  const freq = midiToHz(instrument === 'triangle' ? midi + 12 : midi);
  const gain = LEVEL.bass * velocity * (instrument === 'triangle' ? 1.3 : 1);
  return [tone(instrument, freq, gain, held(seconds, 0.8, 0.04, 0.004))];
}

/**
 * A church bell's partials around its strike note: [frequency ratio, gain, decay s]. The hum
 * an octave below, the strike note, the minor third that makes a bell sound like a bell, the
 * fifth and the octave; the low ones ring longest.
 */
const CHIME_PARTIALS: ReadonlyArray<readonly [number, number, number]> = [
  [0.5, 0.09, 2.4],
  [1, 0.14, 1.8],
  [1.19, 0.08, 1.4],
  [1.5, 0.06, 1],
  [2, 0.05, 0.7],
];

const drumCache = new Map<string, readonly VoiceSpec[]>();

/** Voices of a drum hit (cached: the same few hits repeat all the time). */
export function drumVoices(
  kit: DrumKit,
  drum: DrumKind,
  velocity: number,
  midi: number,
): readonly VoiceSpec[] {
  const key = `${kit}:${drum}:${velocity}:${midi}`;
  let voices = drumCache.get(key);
  if (!voices) {
    voices = buildDrum(kit, drum, velocity, midi);
    drumCache.set(key, voices);
  }
  return voices;
}

function buildDrum(kit: DrumKit, drum: DrumKind, v: number, midi: number): readonly VoiceSpec[] {
  const soft = kit === 'soft' ? 0.7 : 1;
  switch (drum) {
    case 'kick':
      return kit === 'electro'
        ? [
            tone('sine', 180, 0.55 * v, pluck(0.34, 0.002), 42, 0.12),
            tone('triangle', 1500, 0.12 * v, pluck(0.02, 0.001), 200, 0.015),
          ]
        : [
            tone('sine', 160, 0.5 * v * soft, pluck(0.26, 0.002), 48, 0.09),
            tone('triangle', 1400, 0.1 * v * soft, pluck(0.02, 0.001), 200, 0.015),
          ];
    case 'snare':
      return [
        noise('bandpass', kit === 'soft' ? 1500 : 1900, 0.24 * v * soft, pluck(0.14, 0.001), 0.7),
        tone('triangle', 190, 0.12 * v * soft, pluck(0.07, 0.001)),
      ];
    case 'clap':
      return [
        noise('bandpass', 1300, 0.26 * v, pluck(0.11, 0.001), 1.3),
        { ...noise('bandpass', 1300, 0.2 * v, pluck(0.1, 0.001), 1.3), at: 0.012 },
      ];
    case 'hat':
      return [
        noise('highpass', kit === 'soft' ? 9000 : 8000, 0.07 * v * soft, pluck(0.035, 0.001)),
      ];
    case 'openHat':
      return [noise('highpass', 7000, 0.055 * v * soft, pluck(0.16, 0.002))];
    case 'tom': {
      const freq = midiToHz(midi || 50);
      return [
        tone('sine', freq, 0.32 * v * soft, pluck(0.18, 0.002), freq * 0.65, 0.15),
        tone('triangle', freq * 4, 0.05 * v * soft, pluck(0.02, 0.001)),
      ];
    }
    case 'crash':
      return [noise('highpass', 4500, 0.07 * v * soft, pluck(0.9, 0.002))];
    case 'chime': {
      // A church bell: the inharmonic partials of a bell, the low ones ringing longest.
      const freq = midiToHz(midi || 50);
      return CHIME_PARTIALS.map(([ratio, gain, decay]) =>
        tone('sine', freq * ratio, gain * v, pluck(decay, 0.003)),
      );
    }
    case 'sleigh':
      // A shake of sleigh bells: a bright jingle with a little metallic ting in it.
      return [
        noise('bandpass', 7800, 0.12 * v, pluck(0.12, 0.002), 1.6),
        tone('sine', 3520, 0.025 * v, pluck(0.07, 0.001)),
      ];
    default:
      drum satisfies never;
      return [];
  }
}

function tone(
  wave: Wave,
  freq: number,
  gain: number,
  env: ToneVoice['env'],
  freqTo?: number,
  glide?: number,
): ToneVoice {
  const voice: ToneVoice = { kind: 'tone', wave, at: 0, freq, gain, env };
  if (freqTo !== undefined) {
    voice.freqTo = freqTo;
    voice.glide = glide ?? 0.1;
  }
  return voice;
}

function noise(
  filter: BiquadFilterType,
  freq: number,
  gain: number,
  env: NoiseVoice['env'],
  q?: number,
): NoiseVoice {
  const voice: NoiseVoice = { kind: 'noise', at: 0, filter, freq, gain, env };
  if (q !== undefined) voice.q = q;
  return voice;
}
